import { Position } from '@com/type'
import { TrackFrameUnit, TrackGap, TrackItem, TrackSegment } from '../types'
import { frame2px, px2frame } from './frame'
import { TimelineGap } from '@com/components/timeline/type'
import {
  getGapOffsetFrames,
  resolveGapFrames,
} from '@com/components/timeline/shared'

// 轨道的位置信息（用在轨道区域，渲染轨道时候要用到的数据信息）
export type TrackPosition = {
  top: number
  bottom: number
}

export interface TrackPositionOptions<T extends TrackItem = TrackItem> {
  id: T['id']
  tracks: T[]
  gap: TrackGap
}

export interface SegmentPositionOptions<T extends TrackItem = TrackItem>
  extends Pick<TrackPositionOptions<T>, 'gap' | 'tracks'> {
  frameWidth: number
  gaps: TimelineGap[]
}

export function getTrackPosition(options: TrackPositionOptions): TrackPosition {
  const { id: trackId, tracks, gap } = options

  const targetTrackIndex = tracks.findIndex((track) => track.id === trackId)
  const targetTrack = tracks[targetTrackIndex]!
  const targetTrackHeight = targetTrack.height
  // 计算每个轨道的高度（每个高度都会跟轨道的类型相关）
  const top = tracks.slice(0, targetTrackIndex).reduce((h, track) => {
    return (h += track.height + gap)
  }, 0)

  const bottom = top + targetTrackHeight
  return { top, bottom }
}

export function pxToSegmentStart(
  x: number,
  options: {
    frameWidth: number
    gaps: TimelineGap[]
  },
): TrackFrameUnit {
  const { frameWidth, gaps } = options

  return frameToSegmentStart(px2frame(x, frameWidth), {
    gaps,
  })
}

export function frameToSegmentStart(
  f: number,
  options: {
    gaps: TimelineGap[]
  },
): TrackFrameUnit {
  const { gaps: _gaps } = options
  const gaps = [..._gaps].sort((a, b) => a.in - b.in)

  let r = 0
  for (let index = 0; index < gaps.length; index++) {
    const g = gaps[index]!
    if (r + g.in + resolveGapFrames(g) < f) {
      r += resolveGapFrames(g)
    } else if (r + g.in < f) {
      return (f - (r + g.in)) / g.frames > 0.5 ? g.in + 1 : g.in
    }
  }

  return f - r
}

export function getSegmentPosition(
  segment: TrackSegment,
  options: SegmentPositionOptions,
): Position {
  const { frameWidth, tracks, gap, gaps } = options
  const beforeGapsFrames = getGapOffsetFrames(segment.start, gaps)

  const x = frame2px(segment.start + beforeGapsFrames, frameWidth)

  const trackPositon = getTrackPosition({
    id: segment.trackId,
    tracks,
    gap,
  })

  return { x, y: trackPositon.top }
}
